/**
 * RatingStars — fractional star renderer (no extra deps).
 *
 * <RatingStars rating={4.3} />
 * <RatingStars rating={4.3} count={128} />
 * <RatingStars rating={4.3} size={20} variant="solid" />
 *
 * The "compact" variant renders as a chip (e.g. "★ 4.3 · 128") — the form
 * used on store cards / restaurant tiles where the full row of stars would
 * be too noisy.
 */
import React from 'react';
import { Star } from 'lucide-react';
import { css } from './tokens';

export interface RatingStarsProps {
  rating: number;
  /** Optional review count rendered after the stars (e.g. "(128)"). */
  count?: number;
  /** Star size in pixels. */
  size?: number;
  /** Maximum stars (default 5). */
  max?: number;
  /** Stars rendered or chip layout. */
  variant?: 'stars' | 'chip' | 'compact';
  /** Color of the filled portion. Defaults to brand rating gold. */
  color?: string;
  /** Hide the numeric value (e.g. "4.3") next to the stars. */
  hideValue?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const RatingStars: React.FC<RatingStarsProps> = ({
  rating,
  count,
  size = 14,
  max = 5,
  variant = 'compact',
  color = css.color.rating,
  hideValue = false,
  className,
  style,
}) => {
  const safe = Math.max(0, Math.min(rating, max));
  const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(1) : '0.0');

  // Chip variant — used by store cards / restaurant tiles.
  if (variant === 'chip') {
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: css.color.primary,
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: 12,
          padding: '2px 6px',
          borderRadius: 4,
          letterSpacing: 0.2,
          ...style,
        }}
      >
        <Star size={size - 2} fill="#FFFFFF" strokeWidth={0} />
        {fmt(safe)}
        {count != null && (
          <span style={{ opacity: 0.85, fontSize: 11, marginLeft: 2 }}>
            · {count}
          </span>
        )}
      </span>
    );
  }

  // Compact (default) — single gold star + numeric value + optional count.
  if (variant === 'compact') {
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          color: css.color.text,
          fontSize: 12,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          ...style,
        }}
      >
        <Star size={size} fill={color} stroke={color} />
        {!hideValue && fmt(safe)}
        {count != null && (
          <span style={{ color: css.color.textMuted, fontWeight: 500 }}>
            ({count.toLocaleString('en-IN')})
          </span>
        )}
      </span>
    );
  }

  // Full stars variant — N icons with fractional fill via overlapping.
  const stars: React.ReactNode[] = [];
  for (let i = 0; i < max; i++) {
    const fillPct = Math.max(0, Math.min(1, safe - i));
    stars.push(
      <span key={i} style={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
        <Star size={size} stroke={color} fill="transparent" />
        {fillPct > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: `${fillPct * 100}%`,
              height: '100%',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <Star size={size} stroke={color} fill={color} />
          </span>
        )}
      </span>,
    );
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        color: css.color.text,
        ...style,
      }}
      aria-label={`${fmt(safe)} out of ${max} stars${count != null ? `, ${count} reviews` : ''}`}
    >
      <span style={{ display: 'inline-flex', gap: 1 }}>{stars}</span>
      {!hideValue && <span style={{ fontWeight: 600 }}>{fmt(safe)}</span>}
      {count != null && (
        <span style={{ color: css.color.textMuted, fontWeight: 500 }}>
          ({count.toLocaleString('en-IN')})
        </span>
      )}
    </span>
  );
};

export default RatingStars;

